import { useState } from "react";
import { MapPin, Phone, Mail, Instagram, Facebook, MessageCircle } from "lucide-react";
import { useLang } from "@/App";
import { all, pick } from "@/data/images";
import { TEL1, TEL2, TEL1_TEL, TEL2_TEL, EMAIL_RESERVAS, IG, FB, MAPS_LINK, MAPS_IFRAME } from "@/data/site";
import Hero from "@/components/Hero";
import { Reveal, Pretitulo, BtnPrimario } from "@/components/Reveal";
import { apiUrl } from "@/lib/api";

export default function Contacto() {
  const { t, lang } = useLang();
  const p = t.contacto;
  const [form, setForm] = useState({ nombre: "", pais: "", provincia: "", email: "", telefono: "", mensaje: "" });
  const [estado, setEstado] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const enviar = async (e) => {
    e.preventDefault();
    setEstado("enviando");
    try {
      const r = await fetch(apiUrl("/api/contacto"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, idioma: lang }),
      });
      if (!r.ok) throw new Error();
      setEstado("ok");
      setForm({ nombre: "", pais: "", provincia: "", email: "", telefono: "", mensaje: "" });
    } catch {
      setEstado("error");
    }
  };

  const CAMPOS = [
    ["nombre", "nombre", true],
    ["pais", "pais", true],
    ["provincia", "provincia", true],
    ["email", "email", true, "email"],
    ["telefono", "telefono", true, "tel"],
    ["mensaje", "mensaje", false, null, 4],
  ];

  return (
    <div data-testid="pagina-contacto">
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid="hero-contacto" />

      {/* 2. Datos + formulario */}
      <section className="py-20 md:py-28" data-testid="c-datos-form">
        <div className="mx-auto grid max-w-[1200px] gap-10 px-6 md:px-10 lg:grid-cols-2 lg:gap-14">
          <Reveal>
            <div className="h-full bg-ink p-9 md:p-12" data-testid="c-datos-bloque">
              <p className="text-[11px] font-bold tracking-[0.24em] text-terra">{p.datosTitulo}</p>
              <ul className="mt-9 space-y-7 text-sm">
                <li>
                  <a href={`https://wa.me/${TEL1_TEL.replace("+", "")}`} target="_blank" rel="noreferrer" className="group flex items-start gap-4" data-testid="c-whatsapp">
                    <MessageCircle size={20} strokeWidth={1.4} className="mt-0.5 text-terra" />
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-sand/50">{p.whatsapp}</span>
                      <span className="mt-1 block text-sand group-hover:text-terra">{TEL1}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={`tel:${TEL1_TEL}`} className="group flex items-start gap-4" data-testid="c-tel1">
                    <Phone size={20} strokeWidth={1.4} className="mt-0.5 text-terra" />
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-sand/50">{t.global.footer.telefono} 1</span>
                      <span className="mt-1 block text-sand group-hover:text-terra">{TEL1}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={`tel:${TEL2_TEL}`} className="group flex items-start gap-4" data-testid="c-tel2">
                    <Phone size={20} strokeWidth={1.4} className="mt-0.5 text-terra" />
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-sand/50">{t.global.footer.telefono} 2</span>
                      <span className="mt-1 block text-sand group-hover:text-terra">{TEL2}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a href={`mailto:${EMAIL_RESERVAS}`} className="group flex items-start gap-4" data-testid="c-email">
                    <Mail size={20} strokeWidth={1.4} className="mt-0.5 text-terra" />
                    <span>
                      <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-sand/50">{t.global.footer.correo}</span>
                      <span className="mt-1 block break-all text-sand group-hover:text-terra">{EMAIL_RESERVAS}</span>
                    </span>
                  </a>
                </li>
                <li className="flex items-start gap-4 text-sand" data-testid="c-ubicacion">
                  <MapPin size={20} strokeWidth={1.4} className="mt-0.5 shrink-0 text-terra" />
                  <span>{p.datos.direccion}</span>
                </li>
                <li className="flex gap-5 pt-2">
                  <a href={IG} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-sand hover:text-terra" data-testid="c-instagram"><Instagram size={20} strokeWidth={1.4} /></a>
                  <a href={FB} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-sand hover:text-terra" data-testid="c-facebook"><Facebook size={20} strokeWidth={1.4} /></a>
                </li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={enviar} className="border border-lineas bg-sand-deep p-9 md:p-12" data-testid="c-form">
              <h2 className="font-serif text-3xl font-light text-ink">{p.form.titulo}</h2>
              <div className="mt-8 space-y-5">
                {CAMPOS.map(([key, label, required, type, rows]) => (
                  <div key={key}>
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-mute">{p.form.campos[label]}</label>
                    {rows ? (
                      <textarea
                        rows={rows}
                        required={required}
                        value={form[key]}
                        onChange={set(key)}
                        className="mt-2 w-full border border-lineas bg-sand px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra"
                        data-testid={`c-form-${key}`}
                      />
                    ) : (
                      <input
                        type={type || "text"}
                        required={required}
                        value={form[key]}
                        onChange={set(key)}
                        className="mt-2 w-full border border-lineas bg-sand px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-terra"
                        data-testid={`c-form-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <BtnPrimario onClick={enviar} testid="c-form-enviar-btn">{p.form.btn}</BtnPrimario>
              </div>
              {estado === "ok" && <p className="mt-5 text-sm font-semibold text-cactus" data-testid="c-form-ok">{p.form.ok}</p>}
              {estado === "error" && <p className="mt-5 text-sm font-semibold text-terra" data-testid="c-form-error">{p.form.error}</p>}
            </form>
          </Reveal>
        </div>
      </section>

      {/* 3. Ubicación */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="c-ubicacion">
        <div className="mx-auto grid max-w-[1300px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[5fr_7fr]">
          <Reveal>
            <Pretitulo center={false}>{p.ubicacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{p.ubicacion.titulo}</h2>
            <p className="mt-7 max-w-xl text-[15.5px] leading-relaxed text-ink-soft">{p.ubicacion.cuerpo}</p>
            <div className="mt-9">
              <BtnPrimario href={MAPS_LINK} testid="c-como-llegar-btn">{p.ubicacion.btn}</BtnPrimario>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="border border-lineas bg-white p-2 shadow-lg shadow-ink/5" data-testid="c-mapa">
              <iframe src={MAPS_IFRAME} width="100%" height="420" style={{ border: 0 }} allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" title="Mapa" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}